import { ref, computed } from "vue";
import { useStorage, useSessionStorage } from "@vueuse/core";
import { defineStore } from "pinia";

export const useQuizStore = defineStore("quiz", () => {
  const quiz = ref({});

  const isDev = document.location.hostname.match(/(\.test|localhost)/);

  const storage = useStorage;

  const question = ref({});
  const questionId = ref(null);

  const userId = ref(null);
  const user = ref(null);

  const quizId = isDev ? storage("quizId", ref(null)) : ref(null);
  const answers = isDev ? storage("answer", ref([])) : ref([]);
  const startedAt = isDev ? storage("startedAt", ref(0)) : ref(0);
  const currentSegmentId = isDev
    ? storage("currentSegmentId", ref(null))
    : ref(null);
  const currentQuestionId = isDev
    ? storage("currentQuestionId", ref(null))
    : ref(null);
  const currentQuestionStartAt = ref(null);

  const currentRoute = isDev ? storage("currentRoute", ref({})) : ref({});

  function setCurrentRoute(name, params) {
    // console.log("setCurrentRoute(", name, params);
    currentRoute.value = { name, params };
  }

  function setUserId(value) {
    userId.value = value;
  }
  function setUser(value) {
    user.value = value;
  }
  function setStartedAt(value) {
    startedAt.value = value;
  }

  function setQuestion(a) {
    question.value = a;
    questionId.value = question.value.id;
  }
  function setQuiz(a) {
    quiz.value = a;
    if (quiz.value.id != quizId.value) reset();
    quizId.value = quiz.value.id;
  }
  function setStatus(segmentId, questionId) {
    currentSegmentId.value = parseInt(segmentId);
    currentQuestionId.value = parseInt(questionId);
    currentQuestionStartAt.value = Date.now();
  }

  function setAnswer(choices, question) {
    answers.value.push({
      segment_id: parseInt(currentSegmentId.value),
      question_id: parseInt(currentQuestionId.value),
      choices,
      duration: Date.now() - currentQuestionStartAt.value,
      score: getQuestionScore(question, { choices }),
    });
  }

  function nextQuestion() {
    let nextSegmentID = currentSegmentId.value;
    let nextQuestionID = currentQuestionId.value;

    // console.log("CURRENT", currentSegmentId.value, currentQuestionId.value);

    if (!nextSegmentID) {
      nextSegmentID = currentSegmentId.value = quiz.value.segments[0].id;
      // console.log("First Segment");
      nextQuestionID = currentQuestionId.value = null;
    }
    if (nextQuestionID == 0) {
      // console.log("Next Segment");
      const currentSegmentIndex = quiz.value.segments.findIndex(
        (segment) => segment.id == nextSegmentID
      );
      if (currentSegmentIndex < quiz.value.segments.length - 1) {
        nextSegmentID = quiz.value.segments[currentSegmentIndex + 1].id;
        nextQuestionID = null;
      } else {
        return {
          segmentId: 0,
          questionId: null,
        };
      }
    }
    if (nextQuestionID === null) {
      if (!quiz.value.segments) return;
      nextQuestionID = quiz.value.segments.find(
        (segment) => segment.id == nextSegmentID
      ).questions[0].id;
      // console.log("First Question of segment");
      return {
        segmentId: nextSegmentID,
        questionId: nextQuestionID,
      };
    }

    const currentQuestionIndex = quiz.value.segments
      .find((segment) => segment.id == nextSegmentID)
      .questions.findIndex((question) => question.id == nextQuestionID);
    if (
      currentQuestionIndex <
      quiz.value.segments.find((segment) => segment.id == nextSegmentID)
        .questions.length -
        1
    ) {
      // console.log("Next Question of segment");
      nextQuestionID = quiz.value.segments.find(
        (segment) => segment.id == nextSegmentID
      ).questions[currentQuestionIndex + 1].id;
    } else {
      // console.log("End of Segment");
      nextQuestionID = 0;
    }

    return {
      segmentId: nextSegmentID,
      questionId: nextQuestionID,
    };
  }

  function getRoute(params) {
    if (params.segmentId && params.questionId > 0) {
      return {
        name: "segment-question",
        params,
      };
    }
    if (params.segmentId) {
      return {
        name: "segment-end",
        params: {
          segmentId: params.segmentId,
        },
      };
    }
  }
  function currentSegment() {
    var tmp = quiz.value.segments.find(
      (segment) => segment.id === currentSegmentId.value
    );
    if (tmp == null) reset();
    return tmp;
  }
  function currentQuestion() {
    if (currentSegment())
      return currentSegment().questions.find(
        (question) => question.id === currentQuestionId.value
      );
  }

  function getScore() {
    let total_score = 0,
      max_score = 0;
    getScores().forEach((score, idx) => {
      /*
      // on ignore le premier segment
      if (idx == 0) return;
      if (score.score == null) return;
      total_score += score.score;
      max_score += score.max;
      */
      // on ne prend que le segment courant ( le dernier ayant un score non-null)
      if (score.score !== null) {
        total_score = score.score;
        max_score = score.max;
      }
    });
    if (!max_score || !total_score) return 0;
    return Math.min(100, Math.round((total_score / max_score) * 100));
  }
  function getScores() {
    const scores = [];
    if (!quiz.value.segments) return scores;

    quiz.value.segments.forEach((segment, idx) => {
      scores.push({ segmentId: segment.id, ...getSegmentScore(segment.id) });
    });
    return scores;
  }

  function getSegmentScore(segmentId) {
    if (!quiz.value.segments) return { score: null, max: 0 };
    const segment = quiz.value.segments.find(
      (segment) => segment.id == segmentId
    );
    if (!segment) return { score: null, max: 0 };
    const segmentAnswers = answers.value.filter(
      (answer) => answer.segment_id == segmentId
    );
    if (!segmentAnswers.length)
      return { score: null, max: segment.questions.length };
    let score = 0;
    segmentAnswers.forEach((answer) => {
      const question = segment.questions.find(
        (question) => question.id == answer.question_id
      );
      score += getQuestionScore(question, answer);
    });
    return { score, max: segment.questions.length };
  }
  function getQuestionScore(question, answer) {
    if (question)
      switch (question.type) {
        default:
          return 0;
          break;
        case "QPA":
          // console.log("QPA", {
          //   goodAnswers: question.answers.map((a) => a.id),
          //   userAnswers: answer.choices,
          // });

          const leftList = question.answers
            .filter(({ column }) => column == 0)
            .map(({ id }) => id);
          const rightList = question.answers
            .filter(({ column }) => column == 1)
            .map(({ id }) => id);

          return leftList.length ==
            Object.entries(answer.choices).filter(
              ([key, value]) =>
                leftList.indexOf(parseInt(key)) ==
                rightList.indexOf(parseInt(value))
            ).length
            ? 1
            : 0;
          break;
        case "QPO":
          return compareArrays(
            question.answers.map((a) => a.id),
            answer.choices
          )
            ? 1
            : 0;
          break;
        case "QRS":
          if (answer.choices.length === 0) return 0;
          // console.log(answer.choices, question.answers);
          const best = question.answers
            .map((choice) => {
              choice.distance = levenshteinDistance(
                answer.choices[0].toLowerCase(),
                choice.title.toLowerCase()
              );
              return choice;
            })
            .sort((a, b) => a.distance - b.distance)[0].distance;
          return best < answer.choices[0].length / 5;
        case "QCU":
          const goodAnswersQCU = question.answers
            .filter((choice) => choice.valid)
            .map((choice) => choice.id)
            .sort();
          return goodAnswersQCU.includes(answer.choices[0]) ? 1 : 0;
        case "QCM":
          const goodAnswers = question.answers
            .filter((choice) => choice.valid)
            .map((choice) => choice.id)
            .sort();
          return compareArrays(goodAnswers, answer.choices.sort()) ? 1 : 0;
      }
  }

  const levenshteinDistance = (s, t) => {
    if (!s.length) return t.length;
    if (!t.length) return s.length;
    const arr = [];
    for (let i = 0; i <= t.length; i++) {
      arr[i] = [i];
      for (let j = 1; j <= s.length; j++) {
        arr[i][j] =
          i === 0
            ? j
            : Math.min(
                arr[i - 1][j] + 1,
                arr[i][j - 1] + 1,
                arr[i - 1][j - 1] + (s[j - 1] === t[i - 1] ? 0 : 1)
              );
      }
    }
    return arr[t.length][s.length];
  };
  const compareArrays = (a, b) => {
    if (a.length !== b.length) return false;
    else {
      // Comparing each element of your array
      for (var i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) {
          return false;
        }
      }
      return true;
    }
  };

  function reset() {
    console.log("RESET");
    answers.value = [];
    currentSegmentId.value = null;
    currentQuestionId.value = null;
  }

  const all_question_types = [
    "QCM",
    "QPO",
    "QPA",
    "QCU",
    "QRS",
    // "QRL"
  ];

  return {
    quiz,
    answers,
    startedAt,
    currentRoute,
    currentSegmentId,
    currentQuestionId,
    all_question_types,
    question,
    questionId,
    userId,
    user,
    getRoute,
    currentSegment,
    currentQuestion,
    setQuiz,
    setCurrentRoute,
    setStatus,
    setAnswer,
    nextQuestion,
    reset,
    getScore,
    getScores,
    getSegmentScore,
    setStartedAt,
    setQuestion,
    getQuestionScore,
    setUserId,
    setUser,
  };
});
